/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import styles from "./BGLevelSummary.module.css";
import Expand from "../../../../../assets/images/expand.svg";
import Chevron from "../../../../../assets/images/Chvron.svg";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import {
  groupByBG,
  initOpenState,
  SUMMARY_NUM_COLS as numCols,
  SUMMARY_PERCENT_COLS as percentCols,
  formatPercent,
} from "./channelSummaryUtils";

export default function ChannelLevelSummary({
  payload,
  summaryLevel = "",
  levelOptions = [],
  onLevelChange = () => { },
  loading,
  CaretComponent = ArrowDropDownIcon,
}) {
  let filteredPercentCols =
    summaryLevel === "Channel"
      ? percentCols.filter((c) => c !== "Predictive Allocation %")
      : percentCols;

  const filteredNumCols =
    summaryLevel === "Channel"
      ? numCols.filter((c) => c !== "Feasible Loss Mitigation")
      : numCols;

  const [expandedView, setExpandedView] = useState(false);
  let levelkey = summaryLevel === "Channel" ? "Level1" : "BG";
  const hasLevelOptions = Array.isArray(levelOptions) && levelOptions.length > 0;

  const { groups, orderedBGs } = useMemo(
    () => groupByBG(payload?.data ?? [], levelkey),
    [payload, levelkey]
  );

  const [open, setOpen] = useState(() => initOpenState(orderedBGs));

  // ✅ FORMATTERS (ADDED)
  const formatToCappedUnit = (value) => {
    if (value === null || value === undefined) return "-";
    if (value < 1000) return `${value}`;
    if (value < 100000) return `${(value / 1000).toFixed(1)} K`;
    if (value < 10000000) return `${(value / 100000).toFixed(1)} L`;
    return `${(value / 10000000).toFixed(1)} Cr`;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";

    if (typeof value === "number") {
      if (value >= 10000) return formatToCappedUnit(value);
      return value.toLocaleString("en-IN");
    }

    return value;
  };

  React.useEffect(() => {
    setOpen((prev) => {
      const next = { ...prev };
      let changed = false;

      orderedBGs.forEach((bg) => {
        if (!(bg in next)) {
          next[bg] = false;
          changed = true;
        }
      });

      Object.keys(next).forEach((bg) => {
        if (!orderedBGs.includes(bg)) {
          delete next[bg];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [orderedBGs]);

  const toggle = (bg) => setOpen((s) => ({ ...s, [bg]: !s[bg] }));

  const childLabelKey = summaryLevel === "Channel" ? "BG" : "Small c";

  const renderNumberCells = (row, rowKey) =>
    filteredNumCols.map((c) => (
      <td key={`${rowKey}-${c}`} title={row?.[c]}>
        {formatValue(row?.[c])}
      </td>
    ));

  const renderPercentCells = (row, rowKey) =>
    filteredPercentCols.map((c) => (
      <td key={`${rowKey}-${c}`} className={styles.kpiPct}>
        {formatPercent(row?.[c])}
      </td>
    ));

  const renderChildRow = (bg, row, idx) => (
    <tr key={`${bg}-child-${idx}`} className={styles.childRow}>
      <td className={`${styles.stickyLeft} ${styles.childBW}`}>
        {row?.[childLabelKey] || "-"}
      </td>

      {renderNumberCells(row, `${bg}-child-${idx}`)}
      {renderPercentCells(row, `${bg}-child-${idx}`)}
    </tr>
  );

  const renderGroupRows = (bg) => {
    const grp = groups.get(bg);
    const total = grp?.total || {};
    const children = grp?.children || [];

    return (
      <React.Fragment key={bg}>
        <tr
          className={`${styles.parentRow} ${bg === "TOTAL" ? styles.totalRow : ""
            }`}
        >
          <td className={`${styles.stickyLeft} ${styles.bwCell}`}>
            <button className={styles.chevBtn} onClick={() => toggle(bg)}>
              {bg !== "TOTAL" && (
                <span className={`${styles.chev} ${open[bg] ? styles.chevOpen : ""}`}>
                  <img src={Chevron} alt="Chevron" />
                </span>
              )}
            </button>

            <span className={styles.bwText}>{bg}</span>
          </td>

          {renderNumberCells(total, bg)}
          {renderPercentCells(total, bg)}
        </tr>

        {open[bg] && children.map((row, idx) => renderChildRow(bg, row, idx))}
      </React.Fragment>
    );
  };

  const tableHead = (
    <thead className={styles.thead}>
      <tr>
        <th className={styles.stickyLeft}>{summaryLevel}</th>
        {filteredNumCols.map((c) => (
          <th key={c}>{c}</th>
        ))}
        {filteredPercentCols.map((c) => (
          <th key={c}>{c}</th>
        ))}
      </tr>
    </thead>
  );

  const tableBody = (
    <tbody>
      {orderedBGs.map(renderGroupRows)}
    </tbody>
  );

  const shimmerBody = (
    <tbody>
      <tr className={styles.shimmerRow}>
        <td colSpan={1 + filteredNumCols.length + percentCols.length}>
          <div className={styles.tableShimmer} />
        </td>
      </tr>
    </tbody>
  );

  const noDataAvailable = !payload?.data || payload.data.length === 0;

  const tableMarkup = (useLoader = false) => (
    <table className={styles.table}>
      <colgroup>
        <col className={styles.colBW} />
        {filteredNumCols.map((c) => (
          <col key={`n-${c}`} />
        ))}
        {filteredPercentCols.map((c) => (
          <col key={`p-${c}`} />
        ))}
      </colgroup>

      {tableHead}
      {useLoader ? shimmerBody : tableBody}
    </table>
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.expandWrap}>
        <span className={styles.title}>
          Summary at Channel Level
        </span>

        <div className={styles.rightControls}>
          {/* LEVEL SELECT */}
          <div
            className={`${styles.levelSelectWrap} ${hasLevelOptions ? "" : styles.disabled}`}
          >
            <span className={styles.levelSelectDisplay}>
              <span className={styles.levelPrefix}>Level :</span>
              <span className={styles.levelValue}>
                {summaryLevel || "—"}
              </span>
            </span>

            <span className={styles.levelCaret}>
              <CaretComponent />
            </span>

            <select
              className={styles.levelSelectNative}
              value={summaryLevel || ""}
              onChange={(e) => onLevelChange(e.target.value)}
            >
              {hasLevelOptions ? (
                levelOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))
              ) : (
                <option value="">Loading...</option>
              )}
            </select>
          </div>

          {/* EXPAND BUTTON */}
          <div className={styles.segmentedBtn}>
            <button
              type="button"
              className={styles.segment}
              onClick={() => setExpandedView(true)}
              disabled={noDataAvailable}
            >
              <img src={Expand} alt="Expand" className={styles.icon} />
            </button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className={styles.tableOuter}>
        <div className={styles.scrollArea}>
          {noDataAvailable && !loading ? (
            <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              No data available
            </div>
          ) : (
            tableMarkup(!!loading)
          )}
        </div>
      </div>

      {/* EXPANDED */}
      {expandedView && !noDataAvailable && (
        <button
          className={styles.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setExpandedView(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setExpandedView(false);
          }}
          tabIndex={0}
          aria-label="Close expanded view"
        >
          <div className={styles.zoomCard}>
            <div className={styles.zoomHeader}>
              <div className={styles.zoomTitle}>
                Summary at Channel Level (Expanded)
              </div>

              <button
                className={styles.closeBtn}
                onClick={() => setExpandedView(false)}
              >
                x
              </button>
            </div>

            <div className={styles.zoomScrollArea}>
              {tableMarkup(!!loading)}
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

ChannelLevelSummary.propTypes = {
  payload: PropTypes.shape({
    data: PropTypes.array,
  }),
  summaryLevel: PropTypes.string,
  levelOptions: PropTypes.array,
  onLevelChange: PropTypes.func,
  loading: PropTypes.bool,
  CaretComponent: PropTypes.elementType,
};